/**
 * Game progression — best level + current level cross-session par jeu.
 *
 * Sprint 1.6 (DEC-007 wire-up). Persiste dans localStorage clé
 * `labrain.progression.<gameSlug>` avec versioning forward-compatible.
 *
 * Mécanique :
 *   - `bestLevel` : plus haut niveau converged avec succès (1-50).
 *   - `currentLevel` : niveau choisi pour la prochaine session (libre, mode bibliothèque).
 *   - `promoteOnSuccess` : appelé quand staircase converge (threshold !== null) ; promeut
 *     bestLevel uniquement si l'utilisateur jouait à son best (pas de skip de paliers).
 *
 * Hors scope : sync backend, paywall, unlock conditionnel familier.
 */
import { SyncEngine } from './sync';
const STORAGE_PREFIX = 'labrain.progression.';
const STORAGE_VERSION = 1;
export const MIN_LEVEL = 1;
export const MAX_LEVEL = 50;
function clampLevel(level) {
    if (!Number.isFinite(level))
        return MIN_LEVEL;
    const rounded = Math.round(level);
    if (rounded < MIN_LEVEL)
        return MIN_LEVEL;
    if (rounded > MAX_LEVEL)
        return MAX_LEVEL;
    return rounded;
}
function defaultProgression(gameSlug) {
    return {
        gameSlug,
        bestLevel: MIN_LEVEL,
        currentLevel: MIN_LEVEL,
        lastRunAt: null,
        lastThreshold: null,
    };
}
function storageKey(gameSlug) {
    return `${STORAGE_PREFIX}${gameSlug}`;
}
const memoryStore = new Map();
let storageWarned = false;
function getStorage() {
    if (typeof window === 'undefined')
        return null;
    try {
        const ls = window.localStorage;
        const probeKey = `${STORAGE_PREFIX}__probe__`;
        ls.setItem(probeKey, '1');
        ls.removeItem(probeKey);
        return ls;
    }
    catch {
        if (!storageWarned) {
            // eslint-disable-next-line no-console
            console.warn('[game-progression] localStorage unavailable — falling back to in-memory store');
            storageWarned = true;
        }
        return null;
    }
}
function readPersisted(gameSlug) {
    const storage = getStorage();
    if (!storage) {
        return memoryStore.get(gameSlug) ?? null;
    }
    let raw;
    try {
        raw = storage.getItem(storageKey(gameSlug));
    }
    catch {
        return null;
    }
    if (!raw)
        return null;
    try {
        const parsed = JSON.parse(raw);
        if (parsed.v !== STORAGE_VERSION)
            return null;
        if (typeof parsed.bestLevel !== 'number' || typeof parsed.currentLevel !== 'number') {
            return null;
        }
        return {
            v: STORAGE_VERSION,
            bestLevel: clampLevel(parsed.bestLevel),
            currentLevel: clampLevel(parsed.currentLevel),
            lastRunAt: typeof parsed.lastRunAt === 'string' ? parsed.lastRunAt : null,
            lastThreshold: typeof parsed.lastThreshold === 'number' && Number.isFinite(parsed.lastThreshold)
                ? parsed.lastThreshold
                : null,
        };
    }
    catch {
        return null;
    }
}
function writePersisted(gameSlug, persisted) {
    const storage = getStorage();
    if (!storage) {
        memoryStore.set(gameSlug, persisted);
        return;
    }
    try {
        storage.setItem(storageKey(gameSlug), JSON.stringify(persisted));
    }
    catch {
        memoryStore.set(gameSlug, persisted);
    }
}
function toView(gameSlug, persisted) {
    return {
        gameSlug,
        bestLevel: persisted.bestLevel,
        currentLevel: persisted.currentLevel,
        lastRunAt: persisted.lastRunAt,
        lastThreshold: persisted.lastThreshold,
    };
}
function fromView(p) {
    return {
        v: STORAGE_VERSION,
        bestLevel: clampLevel(p.bestLevel),
        currentLevel: clampLevel(p.currentLevel),
        lastRunAt: p.lastRunAt,
        lastThreshold: typeof p.lastThreshold === 'number' && Number.isFinite(p.lastThreshold)
            ? p.lastThreshold
            : null,
    };
}
export function loadProgression(gameSlug) {
    const persisted = readPersisted(gameSlug);
    if (!persisted)
        return defaultProgression(gameSlug);
    return toView(gameSlug, persisted);
}
export function saveProgression(p) {
    writePersisted(p.gameSlug, fromView(p));
    SyncEngine.pushProgression(p);
}
/**
 * Promeut bestLevel sur staircase converged.
 *
 * Règles :
 *   - level promu seulement si `levelPlayed === currentBest` (pas de skip).
 *   - cap à MAX_LEVEL.
 *   - si user joue plus bas que son best, met à jour lastRunAt + lastThreshold sans toucher bestLevel.
 *   - currentLevel suit la promotion (next session démarre au nouveau best).
 */
export function promoteOnSuccess(gameSlug, levelPlayed, threshold) {
    const current = loadProgression(gameSlug);
    const playedClamped = clampLevel(levelPlayed);
    const next = {
        ...current,
        lastRunAt: new Date().toISOString(),
        lastThreshold: Number.isFinite(threshold) ? threshold : null,
    };
    if (playedClamped === current.bestLevel && current.bestLevel < MAX_LEVEL) {
        next.bestLevel = current.bestLevel + 1;
        next.currentLevel = next.bestLevel;
    }
    saveProgression(next);
    return next;
}
export function setCurrentLevel(gameSlug, level) {
    const current = loadProgression(gameSlug);
    const clamped = clampLevel(level);
    const next = { ...current, currentLevel: clamped };
    saveProgression(next);
    return next;
}
/** Reset complet — utile pour tests + futur "reset progression" UI. */
export function resetProgression(gameSlug) {
    const storage = getStorage();
    if (storage) {
        try {
            storage.removeItem(storageKey(gameSlug));
        }
        catch {
            /* ignored */
        }
    }
    memoryStore.delete(gameSlug);
    return defaultProgression(gameSlug);
}
/**
 * Merge remote progression rows into localStorage. Last-write-wins by updatedAt.
 * Called from useAuth after pull on session refresh.
 */
export function applySyncedProgression(remote) {
    for (const r of remote) {
        const localCurrent = loadProgression(r.gameSlug);
        // Compare DB.updatedAt against local.lastRunAt as a coarse "newer" signal.
        // DB updatedAt is server-stamped, local lastRunAt is client-stamped on success.
        const localTs = localCurrent.lastRunAt ?? '1970-01-01T00:00:00Z';
        const remoteTs = r.updatedAt;
        if (remoteTs > localTs) {
            writePersisted(r.gameSlug, {
                v: STORAGE_VERSION,
                bestLevel: clampLevel(r.bestLevel),
                currentLevel: clampLevel(r.currentLevel),
                lastRunAt: r.lastRunAt,
                lastThreshold: typeof r.lastThreshold === 'number' && Number.isFinite(r.lastThreshold)
                    ? r.lastThreshold
                    : null,
            });
        }
    }
}
//# sourceMappingURL=game-progression.js.map